import type { ApplicationFull, Contact } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { keys, useAppActions, useContacts, useToday } from '../api/hooks';
import { useAi } from '../drafts/useDraft';
import { TextField } from '../components/fields';
import { useUI } from '../components/overlays';
import { Avatar, Icon, safeUrl, type IconName } from '../components/ui';

/** Кнопки связи: Email, LinkedIn, Telegram, звонок (как contactBtns() в прототипе). */
export function ContactButtons({ c }: { c: Pick<Contact, 'email' | 'linkedinUrl' | 'telegram' | 'phone'> }) {
  const tg = c.telegram.replace(/^@/, '').replace(/^https?:\/\/t\.me\//, '');
  const any = c.email || c.linkedinUrl || c.telegram || c.phone;
  if (!any) return <span className="muted sm">Добавьте email или LinkedIn</span>;
  return (
    <>
      {c.email && (
        <a className="btn sm" href={`mailto:${c.email}`}>
          <Icon name="mail" size={16} /> Email
        </a>
      )}
      {c.linkedinUrl && (
        <a className="btn sm" href={safeUrl(c.linkedinUrl)} target="_blank" rel="noopener noreferrer">
          <Icon name="linkedin" size={16} /> LinkedIn
        </a>
      )}
      {c.telegram && (
        <a
          className="btn sm"
          href={`https://t.me/${encodeURIComponent(tg)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="send" size={16} /> Telegram
        </a>
      )}
      {c.phone && (
        <a className="btn sm" href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>
          <Icon name="phone" size={16} /> Позвонить
        </a>
      )}
    </>
  );
}

/** Сами данные контакта: почта, телефон, Telegram, LinkedIn — видны и нажимаются. */
export function ContactDetails({ c }: { c: Pick<Contact, 'email' | 'linkedinUrl' | 'telegram' | 'phone'> }) {
  const tg = c.telegram.replace(/^@/, '').replace(/^https?:\/\/t\.me\//, '');
  const rows: [IconName, string, string][] = [];
  if (c.email) rows.push(['mail', c.email, `mailto:${c.email}`]);
  if (c.phone) rows.push(['phone', c.phone, `tel:${c.phone.replace(/[^\d+]/g, '')}`]);
  if (c.telegram) rows.push(['send', `@${tg}`, `https://t.me/${encodeURIComponent(tg)}`]);
  if (c.linkedinUrl)
    rows.push([
      'linkedin',
      c.linkedinUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, ''),
      safeUrl(c.linkedinUrl),
    ]);
  if (!rows.length) return null;
  return (
    <ul className="cdet">
      {rows.map(([ic, text, href]) => (
        <li key={ic}>
          <Icon name={ic} size={15} />
          <a
            href={href}
            {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {text}
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Поля контакта с сохранением при уходе с поля. */
export function ContactFields({
  c,
  onSave,
}: {
  c: Contact;
  onSave: (f: Partial<Contact>) => Promise<unknown>;
}) {
  return (
    <div className="grid">
      <TextField label="Имя" name="name" value={c.name} onSave={(v) => onSave({ name: v ?? '' })} />
      <TextField
        label="Кто это"
        name="role"
        value={c.role}
        placeholder="HR, рекрутер, тимлид"
        onSave={(v) => onSave({ role: v ?? '' })}
      />
      <TextField
        label="Email"
        name="email"
        type="email"
        value={c.email}
        onSave={(v) => onSave({ email: v ?? '' })}
      />
      <TextField
        label="LinkedIn"
        name="linkedinUrl"
        type="url"
        value={c.linkedinUrl}
        placeholder="ссылка на профиль"
        onSave={(v) => onSave({ linkedinUrl: v ?? '' })}
      />
      <TextField
        label="Telegram"
        name="telegram"
        value={c.telegram}
        placeholder="@ник"
        onSave={(v) => onSave({ telegram: v ?? '' })}
      />
      <TextField
        label="Телефон"
        name="phone"
        type="tel"
        value={c.phone}
        onSave={(v) => onSave({ phone: v ?? '' })}
      />
    </div>
  );
}

function AddContact({ a, onDone }: { a: ApplicationFull; onDone: () => void }) {
  const act = useAppActions();
  const ui = useUI();
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const list = useContacts(q);
  const linked = new Set(a.contacts.map((c) => c.id));
  const candidates = (list.data ?? []).filter((c) => !linked.has(c.id)).slice(0, 20);

  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? '').trim();
    const contact = { name: v('name'), role: v('role'), email: v('email'), linkedinUrl: v('linkedin') };
    if (!contact.name && !contact.email && !contact.linkedinUrl) {
      setErr('Укажите имя, email или LinkedIn');
      return;
    }
    try {
      await act.linkContact(a.id, { contact });
      ui.toast('Контакт добавлен');
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не сохранилось');
    }
  };

  return (
    <div className="pform">
      <label className="f">
        Выбрать из контактов
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Имя или email" />
      </label>
      {candidates.length > 0 && (
        <div className="pick">
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() =>
                act.linkContact(a.id, { contactId: c.id }).then(() => {
                  ui.toast('Контакт привязан');
                  onDone();
                })
              }
            >
              <Avatar name={c.name || c.email} />
              <span>
                <b>{c.name || c.email}</b>{' '}
                <span className="muted sm">
                  {[c.role, c.applications.map((x) => x.company).join(', ')].filter(Boolean).join(' · ')}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <form onSubmit={create} style={{ marginTop: 12 }}>
        <div className="sm muted" style={{ marginBottom: 6 }}>
          или новый контакт
        </div>
        <div className="grid">
          <label className="f">
            Имя
            <input name="name" />
          </label>
          <label className="f">
            Кто это
            <input name="role" placeholder="HR, рекрутер" />
          </label>
          <label className="f">
            Email
            <input name="email" type="email" />
          </label>
          <label className="f">
            LinkedIn
            <input name="linkedin" placeholder="ссылка на профиль" />
          </label>
        </div>
        {err && <p className="fnote err">{err}</p>}
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button className="btn sm primary" type="submit">
            Добавить
          </button>
          <button className="btn sm ghost" type="button" onClick={onDone}>
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
}

export function ContactsSection({ a }: { a: ApplicationFull }) {
  const act = useAppActions();
  const ui = useUI();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  return (
    <section className="psec">
      <div className="psh">
        <h3>Контакт{a.contacts.length > 1 ? 'ы' : ''}</h3>
        {!adding && (
          <button className="btn sm ghost" onClick={() => setAdding(true)}>
            <Icon name="plus" size={15} /> Добавить
          </button>
        )}
      </div>
      {a.contacts.map((c) =>
        editing === c.id ? (
          <div className="pform" key={c.id}>
            <ContactFields c={c} onSave={(f) => act.patchContact(a.id, c.id, f)} />
            <div className="btnrow" style={{ marginTop: 10 }}>
              <button className="btn sm primary" onClick={() => setEditing(null)}>
                Готово
              </button>
              <button
                className="btn sm ghost danger"
                onClick={() =>
                  act.unlinkContact(a.id, c.id).then(() => {
                    setEditing(null);
                    ui.toast('Контакт отвязан от вакансии');
                  })
                }
              >
                Отвязать
              </button>
            </div>
          </div>
        ) : (
          <div className="pcontact" key={c.id}>
            <Avatar name={c.name || c.email} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="who">
                {[c.name || 'Без имени', c.role].filter(Boolean).join(' · ')}
                <button
                  className="icon-btn"
                  style={{ width: 26, height: 26 }}
                  onClick={() => setEditing(c.id)}
                  aria-label="Редактировать контакт"
                >
                  <Icon name="pencil" size={14} />
                </button>
              </div>
              <div className="btnrow">
                <ContactButtons c={c} />
              </div>
            </div>
          </div>
        ),
      )}
      {!a.contacts.length && !adding && (
        <p className="muted" style={{ margin: 0 }}>
          Контакт не указан.
        </p>
      )}
      {adding && <AddContact a={a} onDone={() => setAdding(false)} />}
      <ContactSearch a={a} />
    </section>
  );
}

/** «Найти контакт в интернете» и найденные предложения: добавить или «не тот». */
function ContactSearch({ a }: { a: ApplicationFull }) {
  const act = useAppActions();
  const ui = useUI();
  const qc = useQueryClient();
  const ai = useAi();
  const [busy, setBusy] = useState(false);
  const st = a.contactSearch;
  const running = busy || st?.status === 'running';
  // поиск запустил worker — перечитываем карточку, пока не закончится
  useEffect(() => {
    if (st?.status !== 'running' || busy) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: keys.app(a.id) }), 5000);
    return () => clearInterval(t);
  }, [st?.status, busy, a.id, qc]);
  const enabled = !!useToday().data?.contactSearch;
  const results = st?.results ?? [];
  if (!ai && !results.length) return null;
  if (!enabled && !results.length) return null;
  const search = async () => {
    setBusy(true);
    try {
      const full = await act.findContacts(a.id);
      const n = full.contactSearch?.results.length ?? 0;
      ui.toast(n ? `Найдено: ${n} — проверьте и добавьте` : 'Подходящих контактов не нашлось');
    } catch (x) {
      ui.toast(x instanceof ApiError ? x.message : 'Поиск не удался. Повторите.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="csearch">
      {results.length > 0 && (
        <>
          <div className="csearch-h">
            <Icon name="search" size={15} /> Найдено в интернете — проверьте перед добавлением
          </div>
          {results.map((s, i) => (
            <div className="csug" key={`${s.sourceUrl}-${i}`}>
              <Avatar name={s.name || a.company} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="who">
                  {[s.name || 'Контакт компании', s.role].filter(Boolean).join(' · ')}
                </div>
                {s.why && <p className="muted sm">{s.why}</p>}
                <ContactDetails
                  c={{ email: s.email, linkedinUrl: s.linkedinUrl, telegram: s.telegram, phone: '' }}
                />
                <a className="sm" href={safeUrl(s.sourceUrl)} target="_blank" rel="noopener noreferrer">
                  Источник: {s.sourceUrl.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                </a>
                <div className="btnrow" style={{ marginTop: 6 }}>
                  <button
                    className="btn sm primary"
                    onClick={() =>
                      act.acceptSuggestion(a.id, i).then(
                        () => ui.toast('Контакт добавлен'),
                        () => ui.toast('Не получилось. Повторите.'),
                      )
                    }
                  >
                    <Icon name="plus" size={15} /> Добавить
                  </button>
                  <button className="btn sm ghost" onClick={() => void act.dismissSuggestion(a.id, i)}>
                    Не тот
                  </button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
      {st?.status === 'done' && !results.length && st.note && <p className="muted sm">Поиск: {st.note}</p>}
      {enabled && st?.status === 'error' && <p className="fnote err">{st.error || 'Поиск не удался'}</p>}
      {enabled && (
        <button className="btn sm" onClick={search} disabled={running}>
          <Icon name="search" size={15} />{' '}
          {running
            ? 'Ищу контакты в интернете… до минуты'
            : st
              ? 'Искать ещё раз'
              : 'Найти контакт в интернете'}
        </button>
      )}
    </div>
  );
}
